import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useDeviceType from "../../../custom-hooks/DeviceType";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import {
  plusIcon,
  rightDirectionBlackIcon,
} from "../../../assets";
import Button from "../../../component-lib/Molecules/button/Button";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import EventEHRConfirmationDialog from "../../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import OrderStatusDialog from "../dialogs/OrderStatusDialog";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import { componentKey, setCloseAddOrderStatusDialog } from "../SettingsSlice";
import {
  getOrderStatusList,
  postOrderStatus,
  patchOrderStatus,
  deleteOrderStatus,
} from "../SettingsSaga";
import moment from "moment";

const OrderStatusScreen = () => {
  const { orderStatusList, closeAddOrderStatusDialog } = useSelector(
    (state: any) => state[componentKey]
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [rowPerPage, setRowPerPage] = useState("10");
  const [pageNumber, setPageNumber] = useState(1);
  const [status, setStatus] = useState("");
  const [defaultOrderStatusDataRow, setDefaultOrderStatusDataRow] = useState<any>({});

  // OPEN ADD / EDIT ORDER STATUS DIALOG
  const [dialogState, setDialogState] = useState<null | "add" | "edit">(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const deviceType = useDeviceType();

  // ORDER STATUS COLUMN DATA
  const OrderStatusColumns = [
    {
      field: "status",
      label: <div>Order Status</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.status}</p>;
      },
    },
    {
      field: "createdAt",
      label: <div>Created At</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.createdAt
              ? moment(row?.createdAt).format("MM/DD/YYYY HH:mm")
              : "-"}
          </p>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => (
        <div className="flex items-center">
          <MenuDropdown
            options={[
              {
                label: "Edit",
                onClickCb: () => {
                  setDefaultOrderStatusDataRow(row);
                  setDialogState("edit");
                },
              },
              {
                label: "Delete",
                onClickCb: () => {
                  setSelectedId(row?.id);
                  setOpenConfirmationDialog(true);
                },
              },
            ]}
          />
        </div>
      ),
    },
  ];

  // ORDER STATUS LIST ROW DATA
  const orderStatusRowData = orderStatusList?.orderStatus?.map((data: any) => ({
    id: data?.id || "-",
    status: data?.status || "-",
    createdAt: data?.createdAt || "-",
  }));

  useEffect(() => {
    getOrderStatus();
  }, [rowPerPage, pageNumber, status]);

  useEffect(() => {
    if (closeAddOrderStatusDialog) {
      dispatch(setCloseAddOrderStatusDialog(false));
      setDialogState(null);
      setDefaultOrderStatusDataRow({});
      setOpenConfirmationDialog(false);
      setSelectedId(null);
      getOrderStatus();
    }
  }, [closeAddOrderStatusDialog]);

  const getOrderStatus = () => {
    const payload = {
      pageNumber: pageNumber,
      limit: rowPerPage,
      status: status ? status : "",
    };
    dispatch(getOrderStatusList(payload));
  };

  const addOrderStatus = (data: any) => {
    try {
      const payload = {
        status: data?.status,
      };
      if (dialogState === "edit" && defaultOrderStatusDataRow?.id) {
        dispatch(
          patchOrderStatus({
            id: defaultOrderStatusDataRow.id,
            payload,
          })
        );
      } else {
        dispatch(postOrderStatus(payload));
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <div className="flex items-center gap-3">
            <img
              className="cursor-pointer"
              src={rightDirectionBlackIcon}
              alt="back-arrow"
              style={{ height: "12.5px" }}
              onClick={() => {
                navigate("/settings");
              }}
            />
            <Heading title="Order Status" customStyle={{ color: "#1D1D1D" }} />
          </div>

          <div
            className={`flex ${deviceType === "mobile" ? "w-full justify-between" : ""} gap-3`}
          >
            <div style={{ maxWidth: "350px" }}>
              <SelectDropdown
                label=""
                name="status"
                placeholderValue="All Status"
                displayEmpty={true}
                value={status}
                options={[
                  { label: "All Status", value: "" },
                  ...(orderStatusList?.orderStatus?.map((item: any) => ({
                    label: item.status,
                    value: item.status,
                  })) || []),
                ]}
                onChangeCb={(e) => {
                  setPageNumber(1);
                  setStatus(e.target.value);
                }}
              />
            </div>

            <Button
              startIcon={
                <img
                  src={plusIcon}
                  alt={"add"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label="Add Order Status"
              variant="contained"
              onClickCb={() => {
                setDefaultOrderStatusDataRow({});
                setDialogState("add");
              }}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
              }}
            ></Button>
          </div>
        </div>
        <div style={{ overflow: "auto" }}>
          <Table
            columns={OrderStatusColumns}
            rows={orderStatusRowData}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: orderStatusList?.totalPages,
              totalCount: orderStatusList?.total,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>
      {/* ADD / EDIT ORDER STATUS DIALOG */}
      {dialogState && (
        <OrderStatusDialog
          defaultOrderStatusData={defaultOrderStatusDataRow}
          isOpen={!!dialogState}
          handleClose={() => {
            setDialogState(null);
            setDefaultOrderStatusDataRow({});
          }}
          handleData={(data) => {
            addOrderStatus(data);
          }}
        />
      )}

      {/* DELETE ORDER STATUS CONFIRMATION DIALOG */}
      {openConfirmationDialog && (
        <EventEHRConfirmationDialog
          alertTitle="Delete Order Status"
          alertDescription="Are you sure you want to delete this order status? Please confirm to proceed."
          cancelButtonTitle="Cancel"
          submitButtonTitle="Delete"
          open={openConfirmationDialog}
          handleClose={(action) => {
            if (action === "close") {
              setOpenConfirmationDialog(false);
              setSelectedId(null);
            } else if (action === "submit" && selectedId) {
              dispatch(deleteOrderStatus({ id: selectedId }));
            }
          }}
        />
      )}
    </WrapperCard>
  );
};

export default OrderStatusScreen;

